/**
 * Goal preference fallbacks + HR-zone helper (Stage 10).
 *
 * Single source of truth for the hardcoded defaults that previously lived
 * inline in `MetricRings.svelte`. The native `getGoals` handler returns an
 * all-nil "unset" GoalPreferences on a fresh install; these helpers resolve
 * each field to its effective value with the documented fallback chain.
 */
import type { GoalPreferences } from "@contract/types";

/** Soft weekly distance target (meters). Existing MetricRings default. */
export const DEFAULT_WEEK_TARGET_M = 10_000;
/** Existing MetricRings "hard zone" reference. Also the fallback when ageYears is null. */
export const DEFAULT_HR_REFERENCE_BPM = 180;

/** "Unset" prefs the stub branch returns before the first setGoals call. */
export function unsetGoals(): GoalPreferences {
  return {
    weekDistanceMeters: null,
    peakHrBpm: null,
    ageYears: null,
    updatedAt: null,
  };
}

/** Resolve effective target distance — null prefs fall back to default. */
export function effectiveWeekTargetM(prefs: GoalPreferences | null): number {
  return prefs?.weekDistanceMeters ?? DEFAULT_WEEK_TARGET_M;
}

/** Resolve effective HR reference: explicit peakHrBpm > 220−age > hardcoded 180. */
export function effectiveHrReferenceBpm(prefs: GoalPreferences | null): number {
  if (prefs?.peakHrBpm != null) return prefs.peakHrBpm;
  if (prefs?.ageYears != null) return 220 - prefs.ageYears;
  return DEFAULT_HR_REFERENCE_BPM;
}

/**
 * 5-zone breakdown derived from a resolved max HR. Used by the Peak HR sheet
 * body. Straight % of max (50/60/70/80/90); v0.1 doesn't model resting HR.
 */
export function hrZones(
  max: number,
): Array<{ label: string; lo: number; hi: number }> {
  const pct = (p: number) => Math.round(max * p);
  return [
    { label: "Z1", lo: pct(0.5), hi: pct(0.6) },
    { label: "Z2", lo: pct(0.6), hi: pct(0.7) },
    { label: "Z3", lo: pct(0.7), hi: pct(0.8) },
    { label: "Z4", lo: pct(0.8), hi: pct(0.9) },
    { label: "Z5", lo: pct(0.9), hi: max },
  ];
}
